<template>
  <WdsModal :show="show" class="client-owners-modal" data-test="client-owners-modal" @cancel="close">
    <template #header>
      <WdsStack direction-column gap-xs>
        <WdsHeading type="subtitle" data-test="client-owners-title">
          <template v-if="clientName">
            {{ $t('Client owners for :client', { client: clientName }) }}
          </template>
          <template v-else>
            {{ $t('Client owners for') }}
            <Skeleton
              class="tw-inline-block tw-h-5 tw-w-40 tw-rounded tw-align-middle"
              data-test="client-owners-title-loading"
            />
          </template>
        </WdsHeading>
        <WdsText
          v-if="clientName"
          secondary
          data-test="client-owners-subtitle"
          :text="
            $t(
              'Client owners see :client activity by default. This doesn’t change what they can access. Only account owners and admins can make changes.',
              { client: clientName },
            )
          "
        />
      </WdsStack>
    </template>

    <WdsStack gap-md direction-column>
      <LoadingList v-if="coldLoading" :count="3" avatar no-bg data-test="client-owners-loading" />

      <WdsStack v-else-if="loadError" direction-column gap-sm data-test="client-owners-load-error">
        <WdsAlert error>{{ $t('Could not load the owners.') }}</WdsAlert>
        <div class="tw-flex">
          <WdsButtonGhost error class="-tw-mx-1" data-test="client-owners-retry" @click="refetch()">
            {{ $t('Try again') }}
          </WdsButtonGhost>
        </div>
      </WdsStack>

      <template v-else>
        <WdsAlert v-if="saveError" error data-test="client-owners-error">
          {{ $t('Could not update the owners.') }}
          <template #description>{{ saveError }}</template>
        </WdsAlert>

        <OwnersPicker
          :model-value="selection"
          :candidates="candidates"
          :saved-ids="savedIds"
          :disabled="saving"
          :search-placeholder="$t('Search your team to add a client owner')"
          @update:model-value="draft = $event"
          @search="search = $event"
          @load-more="loadMoreCandidates"
        >
          <template #empty>
            <WdsStack
              direction-column
              align-center
              gap-sm
              class="tw-py-6 tw-text-center"
              data-test="client-owners-empty"
            >
              <WdsAvatar :path="emptyAvatar" xl />
              <WdsText large class="tw-font-semibold" :text="$t('No client owners yet.')" />
              <WdsText
                secondary
                :text="
                  clientName
                    ? $t('Search your team above to add the colleagues who look after :client.', {
                        client: clientName,
                      })
                    : $t('Search your team above to add the colleagues who look after this client.')
                "
              />
            </WdsStack>
          </template>
        </OwnersPicker>
      </template>
    </WdsStack>

    <template #footer>
      <WdsStack justify-space-between align-center class="tw-w-full">
        <WdsButton :disabled="saving" data-test="cancel-owners" @click="close">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton
          primary
          :disabled="saving || draft === null"
          data-test="save-owners"
          @click="confirm"
        >
          <WdsSpinner v-if="saving" :size="16" indeterminate />
          <template v-else>{{ $t('Save changes') }}</template>
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsAvatar,
  WdsButton,
  WdsButtonGhost,
  WdsHeading,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import OwnersPicker, { OwnerSelection } from '@/components/Owners/OwnersPicker.vue'
import { graphql } from '@/gql'
import useOwnerCandidates from '@/composables/useOwnerCandidates'
import { $t } from '@/composables/translation'
import { useToast } from '@/composables/useToast'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import Skeleton from '@/components/Dashboard/Widgets/Utils/Skeleton.vue'
import { toResponsibility } from '@/components/Owners/responsibilities'
import { toOwnerSelection } from '@/components/Owners/fragments'
import { getRandomAvatarPath } from '@/utils/avatar'

const { clientRelationId, show } = defineProps<{
  /** The supplier company's relation or staffing agency's link whose owners are being managed. */
  clientRelationId: string
  show: boolean
}>()

const emit = defineEmits<{ 'update:show': [boolean] }>()

const search = ref('')
const emptyAvatar = getRandomAvatarPath()
// Null until the user edits, so the form shows the saved owners and closing discards nothing saved.
const draft = ref<OwnerSelection[] | null>(null)

const ownersQuery = graphql(`
  query ClientOwners($id: ID!) {
    record: clientRelation(id: $id) {
      id
      client {
        id
        name
      }
      ... on CompanySupplier {
        supplier {
          id
        }
      }
      ... on SupplierClient {
        # Aliased: CompanySupplier.supplier is nullable, so the two cannot share a name.
        agency: supplier {
          id
        }
      }
      ... on Ownable {
        owners {
          id
          ...OwnerFields
        }
      }
    }
  }
`)

const updateOwners = graphql(`
  mutation UpdateClientOwners($input: UpdateOwnersInput!) {
    updateOwners(input: $input) {
      id
      owners {
        id
        ...OwnerFields
      }
    }
  }
`)

const {
  result: ownersResult,
  error: loadError,
  refetch,
} = useQuery(
  ownersQuery,
  () => ({ id: clientRelationId }),
  () => ({ clientId: 'publicApi', enabled: show }),
)

// The account the relation belongs to: a supplier company, or the staffing agency on its own link.
const team = computed(() => {
  const owned = ownersResult.value?.record

  if (!owned) return null

  return 'agency' in owned
    ? { type: 'recruiter' as const, id: owned.agency.id }
    : owned.supplier && { type: 'company' as const, id: owned.supplier.id }
})

// Owners must be members of the supplier account, so its team is who can be picked.
const { candidates, loadMore: loadMoreCandidates } = useOwnerCandidates(() => team.value ?? null, search)

const {
  mutate,
  loading: saving,
  error: mutationError,
} = useMutation(updateOwners, {
  clientId: 'publicApi',
  // The cache updates the owners themselves; filtered lists, the owner filter and the people tab need a refetch.
  refetchQueries: [
    'GraphqlDataTablePaginationQuery',
    'SupplierClientsTableQuery',
    'StaffingAgencyClientsTableQuery',
    'SupplierClientOwnersFilterOptions',
    'ClientTeamOwnersTableQuery',
    'ClientPeopleCounts',
    'ClientDrawerContacts',
  ],
})

// Until the first result lands; a later refetch keeps the list on screen.
const coldLoading = computed(() => !ownersResult.value && !loadError.value)

const savedOwners = computed<OwnerSelection[]>(() =>
  (ownersResult.value?.record?.owners ?? []).map(toOwnerSelection),
)

const savedIds = computed(() => savedOwners.value.map((owner) => owner.id))

const clientName = computed(() => ownersResult.value?.record?.client.name ?? null)

const selection = computed(() => draft.value ?? savedOwners.value)

const saveError = computed(() =>
  mutationError.value
    ? (mutationError.value.graphQLErrors?.[0]?.message ??
      $t('Check your connection and try again.'))
    : '',
)

const close = () => {
  draft.value = null
  emit('update:show', false)
}

const confirm = async () => {
  if (draft.value === null) {
    return
  }

  const result = await mutate(
    {
      input: {
        ownable: clientRelationId,
        // Responsibilities are hidden for now; saved ones pass through untouched.
        owners: draft.value.map((owner) => ({
          user: owner.id,
          responsibility: toResponsibility(owner.responsibility),
        })),
      },
    },
    { context: { skipGlobalErrorHandling: true } },
  ).catch(() => null)

  if (!result) {
    return
  }

  useToast().success($t('Client owners updated'))
  close()
}
</script>

<style lang="scss" scoped>
// The header holds a subtitle too, so the close button would otherwise centre on both lines.
.client-owners-modal :deep(.wds-modal-icon-close) {
  align-self: flex-start;
}
</style>
